import { useTranslation } from "react-i18next";

import { ChannelProvider } from "../contexts/Channel/ChannelsProvider";
import { useDateConstraints } from "../hooks/useDateConstraints";
import { usePopup } from "../hooks/usePopup";

import Icons from "@/components/icons";
import MenuItem from "@/components/layout/menu/MenuItem";
import Menu from "@/components/layout/navbar/Menu";
import { EditChannelCurrentMonthBudgetPopup } from "@/components/ui/v1/popup/EditChannelCurrentMonthBudgetPopup";
import { EditChannelMonthlyBudgetPopup } from "@/components/ui/v1/popup/EditChannelMonthlyBudgetPopup";
import CurrencyCell from "@/components/ui/v1/table/CurrencyCell";
import { Button } from "@/components/ui/v2/button";
import { queryClient } from "@/main";
import { ChannelType } from "@/types/response/ChannelType";
import { TableColumn } from "@/types/table/tableProps";

export const useChannelBudgetPlanningTableColumns = (
  clientId: string,
  selectedMonth: string,
  selectedYear: string
) => {
  const { isCurrentMonth, isNextMonth, canEdit } = useDateConstraints();
  const { openPopup, closePopup, resetPopup } = usePopup();
  const { t } = useTranslation();

  const selectedMonthNum = Number.parseInt(selectedMonth, 10);
  const selectedYearNum = Number.parseInt(selectedYear, 10);

  const isViewingCurrentMonth = isCurrentMonth(
    selectedYearNum,
    selectedMonthNum
  );
  const isViewingNextMonth = isNextMonth(selectedYearNum, selectedMonthNum);
  const canEditSelected = canEdit(selectedYearNum, selectedMonthNum);

  const handleSuccess = (channelId: string) => {
    resetPopup();
    queryClient.invalidateQueries({
      queryKey: ["channelMonths", channelId],
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: ["clientChannels", clientId, selectedMonth, selectedYear],
      exact: false,
    });
  };

  const channelTableColumns: TableColumn<ChannelType>[] = [
    {
      accessor: "name",
      Cell: (row: ChannelType) => <span>{row.name}</span>,
      header: t("common.channel"),
      width: "32.1%",
      sortable: false,
    },
    {
      accessor: "budget",
      Cell: (row: ChannelType) => (
        <CurrencyCell value={row.selectedMonth?.budget ?? 0} />
      ),
      header: t("common.budget"),
      width: "32.1%",
      sortable: false,
    },
    {
      accessor: "spent",
      Cell: (row: ChannelType) => {
        return row.selectedMonth === null ||
          row.selectedMonth?.spent === null ? (
            <span>-</span>
          ) : (
            <CurrencyCell value={row.selectedMonth.spent} />
          );
      },
      header: t("common.spent"),
      width: "40%",
      sortable: false,
    },
    {
      header: t("common.actions"),
      width: "10%",
      sortable: false,
      textAlign: "right",
      Cell: (row: ChannelType) => {
        const menuItems = [
          {
            label: t("actions.edit", {
              entity: isViewingCurrentMonth
                ? t("Current Budget")
                : t("common.forecast"),
            }),
            disable: !canEditSelected || row.selectedMonth?.isInvoiced,
            icon: <Icons.AddIcon fontSize={24} />,
            action: () => {
              const channelId = row.id;
              const budgetForCurrentMonthPopup = row.selectedMonth?.budget ?? 0;

              if (!channelId) {
                console.error("Missing channelId from row", row);
                return;
              }

              if (isViewingCurrentMonth) {
                openPopup(
                  <ChannelProvider
                    channelId={channelId}
                    clientId={clientId}
                    month={selectedMonth}
                    year={selectedYear}
                  >
                    <EditChannelCurrentMonthBudgetPopup
                      budget={budgetForCurrentMonthPopup}
                      channelId={channelId}
                      clientId={clientId}
                      onCancel={closePopup}
                      onSuccess={() => handleSuccess(channelId)}
                    />
                  </ChannelProvider>
                );
              } else if (isViewingNextMonth) {
                openPopup(
                  <ChannelProvider
                    channelId={channelId}
                    clientId={clientId}
                    month={selectedMonth}
                    year={selectedYear}
                  >
                    <EditChannelMonthlyBudgetPopup
                      budget={budgetForCurrentMonthPopup}
                      channelId={channelId}
                      clientId={clientId}
                      onCancel={closePopup}
                      onSuccess={() => handleSuccess(channelId)}
                    />
                  </ChannelProvider>
                );
              }
            },
          },
        ];

        return (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Menu
              trigger={() => (
                <Button variant="ghost" size="icon-sm">
                  <Icons.EllipsisIcon fontSize={24} />
                </Button>
              )}
            >
              {menuItems.map((item) => (
                <MenuItem
                  disabled={item.disable}
                  icon={item.icon}
                  key={item.label}
                  label={item.label}
                  onClick={item.action}
                />
              ))}
            </Menu>
          </div>
        );
      },
    },
  ];

  return {
    channelTableColumns,
  };
};
